// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import {
  CONTROLLABLE_STUDIO_GROUP_TYPE_ID,
  STREAMS_MONITORS_GROUP_TYPE_ID,
} from '../../../../utils/sceneEquipmentsConfig';

/* ============================================================
   ================= EQUIPMENT HANDLE  ====================
   ============================================================ */

const getEquipmentDirectionMap = equipment => {
  const props = equipment?.current_config?.properties || {};
  return {
    input: props.inputs || null,
    output: props.outputs || null,
  };
};

/* ---------- Create Handle---------- */
const createHandle = ({
  nodeId,
  connectionType,
  id,
  config = null,
  decorative = false,
  children = null,
}) => {
  const rfType = connectionType === 'input' ? 'target' : 'source';

  return {
    id: decorative ? id || `${nodeId}_${connectionType}` : id,
    nodeId,
    connectionType,
    rfType,
    decorative,
    displayName: config?.display_name || id,
    protocol: decorative ? null : config?.protocol || null,
    color: config?.color || '',
    tooltipText: config?.tooltipText || '',
    ...(config?.sourceName ? { sourceName: config.sourceName } : {}),
    ...(config?.targetName ? { targetName: config.targetName } : {}),
    ...(decorative && children ? { children } : {}),
  };
};

// Walks parentId up to the root ancestor (a node with no parentId) — the top-level group a
// node belongs to, regardless of how deeply nested it is or which intermediate ancestors are
// currently open/closed.
export const resolveTopLevelNode = (nodeId, nodes = []) => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  let current = nodeMap.get(nodeId);

  while (current?.parentId) {
    current = nodeMap.get(current.parentId);
  }

  return current ?? null;
};

// Resolve the top-level ancestor's group type ID for a node (equipment or group) — used to determine which side its handles render on.
export const resolveTopLevelGroupTypeId = (nodeId, nodes = []) =>
  resolveTopLevelNode(nodeId, nodes)?.data?.state?.equip_group_type_uuid ?? null;

/* ---------- Resolve the side (left/right) a handle renders on ---------- */
export const resolveHandleSide = (handle, { isEquipmentNode, groupTypeId, handleSides } = {}) => {
  const { id, connectionType } = handle;

  // Once connected, a handle faces the node it's connected to.
  const dynamicSide = handleSides?.get(id);

  if (dynamicSide) {
    return dynamicSide;
  }

  if (!isEquipmentNode) {
    const isLeft =
      groupTypeId === CONTROLLABLE_STUDIO_GROUP_TYPE_ID ||
      groupTypeId === STREAMS_MONITORS_GROUP_TYPE_ID;

    return isLeft ? 'left' : 'right';
  }

  return connectionType === 'output' ? 'right' : 'left';
};

// Resolves render sides for a node's whole handle list. A decorative bundle is resolved once —
// using its own dynamic side if connected (group-to-group bundles key handleSides by their own
// id), otherwise a majority vote across its children's dynamic sides, otherwise the direction
// default — and every child inherits that single side. Without this, an equipment bundle's real
// children (each individually facing whichever counterpart it's connected to) can scatter across
// both sides once all shown, crowding one side while leaving the other empty.
export const resolveGroupedHandleSides = (
  handles,
  { isEquipmentNode, groupTypeId, handleSides } = {}
) => {
  const sides = new Map();
  const childGroupSide = new Map();

  handles
    .filter(h => h.decorative && h.children?.length)
    .forEach(group => {
      let groupSide = handleSides?.get(group.id);

      if (!groupSide) {
        const votes = { left: 0, right: 0 };
        group.children.forEach(child => {
          const side = handleSides?.get(child.id);
          if (side) {
            votes[side] += 1;
          }
        });

        groupSide =
          votes.left !== votes.right
            ? votes.left > votes.right
              ? 'left'
              : 'right'
            : resolveHandleSide(group, { isEquipmentNode, groupTypeId });
      }

      sides.set(group.id, groupSide);
      group.children.forEach(child => childGroupSide.set(child.id, groupSide));
    });

  handles.forEach(h => {
    if (sides.has(h.id)) {
      return;
    }
    sides.set(
      h.id,
      childGroupSide.get(h.id) ??
        resolveHandleSide(h, { isEquipmentNode, groupTypeId, handleSides })
    );
  });

  return sides;
};

/* ---------- Find attached handles from output destinations ---------- */
//  Resolve visible node (Recursive)
export const resolveVisibleNode = (nodeId, nodes = []) => {
  if (!nodeId || !nodes.length) {
    return null;
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  let current = nodeMap.get(nodeId);

  while (current) {
    if (!current.hidden) {
      return current;
    }

    current = current.parentId ? nodeMap.get(current.parentId) : null;
  }

  return null;
};

// node.position is relative to its immediate parent, so the canvas-absolute
// center is the sum of every ancestor's position.x plus half of the node's own width.
export const getAbsoluteCenterX = (node, nodes) => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  let x = 0;
  let current = node;
  while (current) {
    x += current.position?.x ?? 0;
    current = current.parentId ? nodeMap.get(current.parentId) : null;
  }

  return x + (node.data?.width ?? 0) / 2;
};

// find handle ID in connected equipment that points to targetNodeId
const findConnectedHandleId = (equipment, targetNodeId, targetNodeHandle, type = 'input') => {
  const settings = equipment?.current_config?.settings || {};

  // If looking for an input connection, check destinations
  // If looking for an output connection, check sources
  const map = type === 'input' ? settings.destinations : settings.sources;
  if (!map) {
    return null;
  }

  for (const [childName, child] of Object.entries(map)) {
    const childUuid = child?.uuid?.equip_setting_val_uuid?.value || child?.uuid?.value;
    const destinationHandle = child?.destination_name?.equip_setting_val_uuid?.value;
    const sourceHandle = child?.source_name?.equip_setting_val_uuid?.value;
    if (
      childUuid === targetNodeId &&
      ((destinationHandle && type !== 'input' && destinationHandle === targetNodeHandle) ||
        (sourceHandle && type === 'input' && sourceHandle === targetNodeHandle))
    ) {
      return childName;
    }
  }

  return null;
};

// Resolve a port's display name directly from equipment properties
const getHandleDisplayName = (equipment, childKey) => {
  const directions = getEquipmentDirectionMap(equipment);

  for (const container of Object.values(directions)) {
    const child = container?.[childKey];
    if (child) {
      const displayNameProp = child?.display_name?.eprop_val_uuid?.value;
      return !displayNameProp || displayNameProp === 'display_name example'
        ? childKey.toUpperCase()
        : displayNameProp;
    }
  }

  return null;
};

/* ---------- Attach connection / destination to handle ---------- */
const attachConnectionToHandle = (handle, settings, equipmentList) => {
  if (!handle || !settings) {
    return handle;
  }
  // Map depends on handle type
  const map = handle.connectionType === 'input' ? settings.sources : settings.destinations;
  if (!map) {
    return handle;
  }

  const child = map[handle.id];

  if (!child) {
    return handle;
  }

  const connectedUuid = child?.uuid?.equip_setting_val_uuid?.value || child?.uuid?.value;
  if (!connectedUuid) {
    return handle;
  }

  const connectedEquip = equipmentList.find(eq => eq.co_equip_uuid === connectedUuid);

  if (!connectedEquip) {
    return handle;
  }

  // Use reusable function to find the handle ID
  const connectedHandleId = findConnectedHandleId(
    connectedEquip,
    handle.nodeId,
    handle.id,
    handle.connectionType
  );

  if (!connectedHandleId) {
    return handle;
  }
  const connectedDisplayName = getHandleDisplayName(connectedEquip, connectedHandleId);

  const displayName =
    connectedDisplayName === 'display_name example' ? connectedHandleId : connectedDisplayName;

  return {
    ...handle,
    tooltipText:
      handle.connectionType === 'output'
        ? `${handle.protocol} to ${displayName}`
        : `${handle.protocol} from ${displayName}`,
    ...(handle.connectionType === 'input'
      ? {
          connection: {
            sourceNodeId: connectedUuid,
            sourceHandleId: connectedHandleId,
            sourceDisplayName: displayName,
          },
          sourceName: connectedEquip.name,
        }
      : {
          destination: {
            targetNodeId: connectedUuid,
            targetHandleId: connectedHandleId,
            targetDisplayName: displayName,
          },
          targetName: connectedEquip.name,
        }),
  };
};

/* ----helper to get color edges/handles based on protocol------ */
const getProtocolColor = protocol => {
  const colors = {
    elevation_v5: 'var(--color-info)',
    carriage_v5: 'var(--color-primary)',
    PTZ_VISCA: 'var(--color-info)',
    RTSP: 'var(--color-success)',
    carriage_position: 'var(--color-primary)',
    default: 'var(--color-light)',
  };

  return colors[protocol] || colors.default;
};

/* ---------- Equipment Handles ---------- */
export const getEquipmentHandles = (equipment, equipmentList, groupsList = []) => {
  if (!equipment?.co_equip_uuid) {
    return [];
  }

  const nodeId = equipment.co_equip_uuid;
  const directions = getEquipmentDirectionMap(equipment);
  const settings = equipment?.current_config?.settings || {};

  return Object.entries(directions).flatMap(([connectionType, container]) => {
    if (!container) {
      return [];
    }

    // dynamically iterate over child nodes
    const handles = Object.entries(container)
      .map(([childKey, child]) => {
        // Dynamically get protocol and display_name from child
        const protocol = child?.protocol?.eprop_val_uuid?.value;
        const displayNameProp = child?.display_name?.eprop_val_uuid?.value;

        if (!protocol) {
          return null;
        }

        const config = {
          // Ignore the example name that comes from the mock services
          display_name:
            !displayNameProp || displayNameProp === 'display_name example'
              ? childKey.toUpperCase()
              : displayNameProp,
          protocol,
          color: getProtocolColor(protocol),
          tooltipText: `${protocol} ${connectionType === 'output' ? 'Output' : 'Input'}`,
        };

        connectionType === 'output'
          ? (config.sourceName = equipment.name)
          : (config.targetName = equipment.name);

        const handle = createHandle({
          nodeId,
          connectionType,
          id: childKey,
          config,
        });

        return attachConnectionToHandle(handle, settings, equipmentList);
      })
      .filter(Boolean)
      .sort((a, b) => a.id.localeCompare(b.id));

    // decorative handle logic ( if more than one handles)
    if (handles.length > 2) {
      return [
        createHandle({
          nodeId,
          connectionType,
          config: {
            display_name: `${equipment.name} (${connectionType})`,
            tooltipText: `${equipment.name} ${connectionType === 'output' ? 'Outputs' : 'Inputs'}`,
          },
          decorative: true,
          children: handles,
        }),
        ...handles,
      ];
    }

    return handles;
  });
};
/* ============================================================
   ================= GROUP HANDLE  ========================
   ============================================================ */

// A group's handles are a merged view of all its child equipment handles, bucketed by which
// external group/equipment they connect to.
const resolveHandleCounterpart = (handle, type, group, resolveGroupFromAnyId) => {
  const rawConnectedId =
    type === 'input' ? handle.connection?.sourceNodeId : handle.destination?.targetNodeId;

  if (!rawConnectedId) {
    return { counterpartId: null, counterpartGroup: null, internal: false };
  }

  const counterpartGroup = resolveGroupFromAnyId(rawConnectedId);

  if (counterpartGroup?.equip_group_uuid === group.equip_group_uuid) {
    return { counterpartId: null, counterpartGroup: null, internal: true };
  }

  return {
    counterpartId: counterpartGroup ? counterpartGroup.equip_group_uuid : rawConnectedId,
    counterpartGroup: counterpartGroup || null,
    internal: false,
  };
};

// Bucket a group's child handles by which external group/equipment they connect to, plus a single merged placeholder for whatever's still unconnected. Each bucket is a single visual handle on the group node, with its children being the real handles on the equipment nodes.
const groupHandlesByCounterpart = (handles, group, resolveGroupFromAnyId) => {
  const connected = new Map();
  const unconnected = { input: [], output: [] };

  handles.forEach(handle => {
    const { connectionType: type } = handle;
    const { counterpartId, counterpartGroup, internal } = resolveHandleCounterpart(
      handle,
      type,
      group,
      resolveGroupFromAnyId
    );

    if (internal) {
      return;
    }

    if (!counterpartId) {
      unconnected[type].push(handle);
      return;
    }

    if (!connected.has(counterpartId)) {
      connected.set(counterpartId, { counterpartId, counterpartGroup, children: [] });
    }
    connected.get(counterpartId).children.push(handle);
  });

  return { connected, unconnected };
};

// Walks up the group hierarchy to find the top-level group a node belongs to, ignoring any intermediate ancestors that are currently collapsed.
const resolveGroupFromAnyId = (id, groupList, facilityEquipmentGroupUuid) => {
  const direct =
    groupList.find(
      g => g.equip_group_uuid === id && g.equip_group_uuid !== facilityEquipmentGroupUuid
    ) ||
    groupList.find(
      g =>
        g.equip_group_uuid !== facilityEquipmentGroupUuid &&
        g.equipment?.some(eq => eq.co_equip_uuid === id)
    );

  if (!direct) {
    return direct;
  }

  // Walk up the parent chain until we reach the top-level group (the one whose parent is the facility group) or a group we've already seen (to avoid infinite loops in case of circular references).
  let current = direct;
  const seen = new Set();
  while (
    current.parent_uuid &&
    current.parent_uuid !== facilityEquipmentGroupUuid &&
    !seen.has(current.equip_group_uuid)
  ) {
    seen.add(current.equip_group_uuid);
    const parent = groupList.find(g => g.equip_group_uuid === current.parent_uuid);
    if (!parent) {
      break;
    }
    current = parent;
  }

  return current;
};

// ============================================================
// ================= GROUP HANDLE  ========================
// ============================================================
export const getEquipmentGroupHandles = (
  group,
  equipmentList,
  groupList,
  facilityEquipmentGroupUuid
) => {
  if (!group?.equip_group_uuid) {
    return [];
  }

  const resolveGroupFromAnyIdInList = id =>
    resolveGroupFromAnyId(id, groupList, facilityEquipmentGroupUuid);

  // ---------------------------------------------------
  // Build all child handles, bucketed by which external group/equipment they connect to —
  // one visual handle per counterpart, plus a single merged placeholder (input+output
  // combined) for whatever's still unconnected, ready to start a new connection of either type.
  // ---------------------------------------------------
  const allHandles = (group.equipment ?? []).flatMap(eq => getEquipmentHandles(eq, equipmentList));
  const { connected, unconnected } = groupHandlesByCounterpart(
    allHandles,
    group,
    resolveGroupFromAnyIdInList
  );

  // get all unconnected children (input + output) to create a single placeholder handle for the group to be able to connect to other groups/equipment
  const unconnectedChildren = [...unconnected.input, ...unconnected.output];

  const openConnectionType = unconnected.output.length ? 'output' : 'input';
  const openDisplayName =
    unconnected.input.length && unconnected.output.length
      ? `${group.name} - (Inputs & Outputs)`
      : `${group.name} - (${openConnectionType === 'output' ? 'Outputs' : 'Inputs'})`;

  // if there are unconnected children, create a single placeholder handle for the group to be able to connect to other groups/equipment
  const placeholders = unconnectedChildren.length
    ? [
        createHandle({
          nodeId: group.equip_group_uuid,
          connectionType: openConnectionType,
          config: {
            display_name: openDisplayName,
            isGroupHandle: true,
            tooltipText: openDisplayName,
            sourceName: group.name,
            targetName: group.name,
          },
          decorative: true,
          children: unconnectedChildren,
        }),
      ].map(handle => ({ ...handle, hidden: connected.size > 0 }))
    : [];

  // Connected handles are grouped by counterpartId(a group node), and each group has a single visual handle on the group node, with its children being the real handles on the equipment nodes.
  const connectedHandles = Array.from(connected.values()).map(
    ({ counterpartId, counterpartGroup, children }) => {
      const inputChildren = children.filter(c => c.connectionType === 'input');
      const outputChildren = children.filter(c => c.connectionType === 'output');

      const type = outputChildren.length > 0 ? 'output' : 'input';

      const groupHandle = createHandle({
        nodeId: group.equip_group_uuid,
        connectionType: type,
        id: `${group.equip_group_uuid}_${counterpartId}`,
        config: { display_name: `${group.name} (${type})` },
        decorative: true,
        children,
      });

      const counterpartName =
        counterpartGroup?.name ?? outputChildren[0]?.targetName ?? inputChildren[0]?.sourceName;

      // Group-level promoted connections always live under `.connection` (both directions),

      if (inputChildren.length > 0 || outputChildren.length > 0) {
        groupHandle.connection = {};
      }

      if (inputChildren.length > 0) {
        groupHandle.connection.sourceNodeId = counterpartId;
        groupHandle.connection.sourceHandleId = counterpartGroup
          ? `${counterpartId}_${group.equip_group_uuid}`
          : inputChildren[0]?.connection?.sourceHandleId;
        groupHandle.connection.sourceType = counterpartGroup ? 'group' : 'equipment';
        groupHandle.sourceName = counterpartName;
      }

      if (outputChildren.length > 0) {
        groupHandle.connection.targetNodeId = counterpartId;
        groupHandle.connection.targetHandleId = counterpartGroup
          ? `${counterpartId}_${group.equip_group_uuid}`
          : outputChildren[0]?.destination?.targetHandleId;
        groupHandle.connection.targetType = counterpartGroup ? 'group' : 'equipment';
        groupHandle.targetName = counterpartName;
      }

      // -----------------------------------
      // Tooltip — lists the real endpoints this handle bundles, on either side
      // -----------------------------------
      const names = children
        .map(child => {
          const connectionDisplay =
            child.connectionType === 'output'
              ? child.destination?.targetDisplayName
              : child.connection?.sourceDisplayName;

          const fallback = child.connectionType === 'output' ? child.targetName : child.sourceName;

          return connectionDisplay || fallback;
        })
        .filter(Boolean);

      const displayNames = names.length > 3 ? [...names.slice(0, 3), '...'] : names;

      groupHandle.tooltipText =
        names.length > 0
          ? `Connected: ${displayNames.join(', ')}`
          : `Connected to ${counterpartName || group.name}`;

      return groupHandle;
    }
  );

  return [...placeholders, ...connectedHandles];
};

export const isSubgroupNode = node => Boolean(node?.parentId);

// Flattens a group node's bundled handles (both counterpart-connected bundles and the shared
// unconnected placeholder) back down to the real, per-equipment handles they wrap.
export const getGroupRealHandles = groupNode => {
  const handles = groupNode?.data?.handles || {};
  return [...(handles.inputs || []), ...(handles.outputs || [])].flatMap(h =>
    h.decorative ? h.children || [] : [h]
  );
};

// Greedily pairs two lists of real (non-decorative) handles by matching protocol and opposite
// direction — one output, one input per pair — each handle used in at most one pair. Used to
// decompose a group-to-group connection into real equipment-to-equipment calls when a subgroup
// is involved and the backend can't resolve the group-level pairing itself.
export const pairComplementaryHandles = (handlesA, handlesB) => {
  const used = new Set();
  const pairs = [];

  (handlesA || []).forEach(a => {
    if (!a.protocol || used.has(a)) {
      return;
    }

    const match = (handlesB || []).find(
      b => !used.has(b) && b.protocol === a.protocol && b.connectionType !== a.connectionType
    );

    if (!match) {
      return;
    }

    used.add(a);
    used.add(match);

    const [output, input] = a.connectionType === 'output' ? [a, match] : [match, a];
    pairs.push({
      source: output.nodeId,
      sourceHandle: output.id,
      target: input.nodeId,
      targetHandle: input.id,
    });
  });

  return pairs;
};

// Looks up a node's handle object (input or output) by id.
const findHandleById = (node, handleId) => {
  const handles = [...(node?.data?.handles?.inputs ?? []), ...(node?.data?.handles?.outputs ?? [])];
  return handles.find(h => h.id === handleId) ?? null;
};

// A handle is free when neither its source (input side) nor destination (output side)
// connection is set.
const isFreeHandle = handle =>
  !handle?.connection?.sourceNodeId && !handle?.destination?.targetNodeId;

// Resolves a connection endpoint down to its real, free, per-equipment handles — a decorative
// id (an equipment's bundle when it has more than two ports in one direction, or any group
// node's own handle, which is always decorative) is UI-only and meaningless to the backend
// outside a pure top-level group-to-group connect. A group considers every free real handle
// across the whole group (matching the "open" bundle semantics), while an equipment node only
// considers the specific handle that was dragged — its free children if it's a bundle, or
// itself.
export const getRealConnectableHandles = (node, handleId) => {
  if (node?.type === 'equipmentGroup') {
    return getGroupRealHandles(node).filter(isFreeHandle);
  }

  const handle = findHandleById(node, handleId);
  if (!handle) {
    return [];
  }

  return (handle.decorative ? (handle.children ?? []) : [handle]).filter(isFreeHandle);
};

/* ============================================================
   ==========  CONNECTIONS LOGIC BETWEEN HANDLES =============
   ============================================================ */
const getHandleProtocols = handle => {
  if (!handle) {
    return [];
  }
  if (handle.decorative) {
    return (handle.children || []).map(c => c.protocol).filter(Boolean);
  }
  return [handle.protocol].filter(Boolean);
};

// Protocols carried by handles of one direction only (drilling into decorative handles'
// children) — the general rule for pairing a handle with an unconnected one: an input can only
// be satisfied by an output of the same protocol, and an output only by an input. Applies the
// same way whether the handles belong to a single piece of equipment or a whole group.
export const getProtocolsByDirection = (handles, connectionType) =>
  new Set(
    (handles || []).filter(h => h.connectionType === connectionType).flatMap(getHandleProtocols)
  );

const hasMatchingProtocols = (protocolsA, protocolsB) =>
  protocolsA.some(p => protocolsB.includes(p));

export const isConnectionAllowed = (connection, currentNodes) => {
  const { source, sourceHandle, target, targetHandle } = connection;
  if (!source || !target || !sourceHandle || !targetHandle) {
    return false;
  }
  if (source === target) {
    return false;
  }
  const sourceNode = currentNodes.find(n => n.id === source);
  const targetNode = currentNodes.find(n => n.id === target);

  if (!sourceNode || !targetNode) {
    return false;
  }

  // ===================================================
  // EQUIPMENT  ↔  EQUIPMENT
  // connections must match protocol on input/output handles to be valid connection
  // ===================================================
  if (sourceNode.type === 'equipment' && targetNode.type === 'equipment') {
    const sourceHandleObj = sourceNode.data?.handles?.outputs?.find(h => h.id === sourceHandle);
    const targetHandleObj = targetNode.data?.handles?.inputs?.find(h => h.id === targetHandle);

    return hasMatchingProtocols(
      getHandleProtocols(sourceHandleObj),
      getHandleProtocols(targetHandleObj)
    );
  }

  // ===================================================
  // EQUIPMENT ↔ EQUIPMENT GROUP  (either drag direction)
  // Valid when the equipment handle's protocols overlap with any protocol
  // among the group's child equipment handles.
  // ===================================================
  if (
    (sourceNode.type === 'equipment' && targetNode.type === 'equipmentGroup') ||
    (sourceNode.type === 'equipmentGroup' && targetNode.type === 'equipment')
  ) {
    const equipNode = sourceNode.type === 'equipment' ? sourceNode : targetNode;
    const groupNode = sourceNode.type === 'equipmentGroup' ? sourceNode : targetNode;
    const equipHandleId = sourceNode.type === 'equipment' ? sourceHandle : targetHandle;
    const groupHandleId = sourceNode.type === 'equipmentGroup' ? sourceHandle : targetHandle;

    const { outputs: equipOutputs = [], inputs: equipInputs = [] } = equipNode.data?.handles || {};
    const equipHandleObj = [...equipOutputs, ...equipInputs].find(h => h.id === equipHandleId);

    const { inputs: groupInputs = [], outputs: groupOutputs = [] } = groupNode.data?.handles || {};
    const groupHandleObj = [...groupInputs, ...groupOutputs].find(h => h.id === groupHandleId);

    if (!equipHandleObj || !groupHandleObj) {
      return false;
    }

    return hasMatchingProtocols(
      getHandleProtocols(equipHandleObj),
      getHandleProtocols(groupHandleObj)
    );
  }

  // ===================================================
  // EQUIPMENT GROUP ↔ EQUIPMENT GROUP
  // Nested equipment must have matching protocols to be valid connection
  // ===================================================
  if (sourceNode.type === 'equipmentGroup' && targetNode.type === 'equipmentGroup') {
    const { outputs: sourceOutputs = [], inputs: sourceInputs = [] } =
      sourceNode.data?.handles || {};
    const { inputs: targetInputs = [], outputs: targetOutputs = [] } =
      targetNode.data?.handles || {};

    const allSourceHandles = [...sourceOutputs, ...sourceInputs];
    const allTargetHandles = [...targetInputs, ...targetOutputs];

    const sourceHandleObj = allSourceHandles.find(h => h.id === sourceHandle);
    const targetHandleObj = allTargetHandles.find(h => h.id === targetHandle);

    if (!sourceHandleObj || !targetHandleObj) {
      return false;
    }

    return hasMatchingProtocols(
      allSourceHandles.flatMap(getHandleProtocols),
      allTargetHandles.flatMap(getHandleProtocols)
    );
  }

  return false;
};

/* ============================================================
   ================= EDGE / CONNECTIONS =======================
   ============================================================ */

const HANDLE_CACHE_MAX = 200;
const handleCache = new Map();

const getEquipmentHandlesWithCache = (equipment, equipmentList, groupList) => {
  if (!equipment?.co_equip_uuid) {
    return [];
  }

  const cacheKey = `${equipment.co_equip_uuid}_${equipment.last_updated}`;

  if (handleCache.has(cacheKey)) {
    return handleCache.get(cacheKey);
  }

  const handles = getEquipmentHandles(equipment, equipmentList, groupList);

  if (handleCache.size >= HANDLE_CACHE_MAX) {
    handleCache.delete(handleCache.keys().next().value);
  }
  handleCache.set(cacheKey, handles);

  return handles;
};

const findNodeData = (nodeId, equipmentList, groupList) => {
  const equipment = equipmentList.find(eq => eq.co_equip_uuid === nodeId);
  if (equipment) {
    return { type: 'equipment', data: equipment };
  }

  for (const group of groupList) {
    const found = group.equipment?.find(eq => eq.co_equip_uuid === nodeId && eq.direct_member);

    if (found) {
      return { type: 'equipment', data: found };
    }
  }

  const group = groupList.find(g => g.equip_group_uuid === nodeId);

  if (group) {
    return { type: 'group', data: group };
  }
  return null;
};

const getHandlesFromNodeData = (nodeData, equipmentList, allGroups, facilityEquipmentGroupUuid) => {
  const { type, data } = nodeData;

  if (type === 'equipment') {
    return getEquipmentHandlesWithCache(data, equipmentList, allGroups);
  }

  if (type === 'group') {
    return getEquipmentGroupHandles(
      data,
      equipmentList,
      allGroups,
      facilityEquipmentGroupUuid
    ).flatMap(h => [h, ...(h.children ?? [])]);
  }

  return [];
};

const getConnectedTargetHandles = handles => handles.filter(h => h.connection?.sourceNodeId);

/* ============================================================
   ================= BUILD EDGE FROM HANDLE ====================
   ============================================================ */

const getConnectionPortName = (handle, side) => {
  const ownName = side === 'target' ? handle?.displayName : handle?.connection?.sourceDisplayName;

  if (!handle?.decorative) {
    return ownName || null;
  }

  const childNames = new Set(
    (handle?.children ?? [])
      .map(child => (side === 'target' ? child.displayName : child.connection?.sourceDisplayName))
      .filter(Boolean)
  );

  return childNames.size === 1 ? [...childNames][0] : null;
};

const getConnectionLabel = ({ handle, sourceNode, targetNode }) => {
  const isSourceGroup = sourceNode?.type === 'equipmentGroup';
  const isTargetGroup = targetNode?.type === 'equipmentGroup';

  // ---------------------------------------------------
  // GROUP → GROUP
  // ---------------------------------------------------
  if (isSourceGroup && isTargetGroup) {
    return `${sourceNode?.data?.state?.name} → ${targetNode?.data?.state?.name}`;
  }

  // ---------------------------------------------------
  // GROUP → EQUIPMENT — the equipment side's name is followed by the connected port name.
  // ---------------------------------------------------
  if ((isSourceGroup && !isTargetGroup) || (!isSourceGroup && isTargetGroup)) {
    const sourceName = sourceNode?.data?.state?.name || handle.sourceName;
    const targetName = targetNode?.data?.state?.name || handle.targetName;
    const portName = getConnectionPortName(handle, isSourceGroup ? 'target' : 'source');

    const equipmentName = isSourceGroup ? targetName : sourceName;
    const equipmentLabel = portName ? `${equipmentName} (${portName})` : equipmentName;

    return isSourceGroup
      ? `${sourceName} → ${equipmentLabel}`
      : `${equipmentLabel} → ${targetName}`;
  }
  // ---------------------------------------------------
  // EQUIPMENT → EQUIPMENT
  // ---------------------------------------------------
  return `${handle.sourceName} (${handle?.connection?.sourceDisplayName}) → ${handle.targetName} (${handle?.displayName})`;
};

export const buildEdgeFromHandle = (handle, nodes, groupList, facilityEquipmentGroupUuid) => {
  const { sourceNodeId, sourceHandleId } = handle?.connection || {};

  if (!sourceNodeId || !sourceHandleId) {
    return null;
  }

  const resolvedSource = resolveVisibleNode(sourceNodeId, nodes);
  const resolvedTarget = resolveVisibleNode(handle.nodeId, nodes);

  if (!resolvedSource || !resolvedTarget) {
    return null;
  }

  const isSourceGroup = resolvedSource.type === 'equipmentGroup';
  const isTargetGroup = resolvedTarget.type === 'equipmentGroup';
  const isGroupBoundary = isSourceGroup && isTargetGroup;

  // Source and target are both top-level groups (direct children of the facility group)
  const topLevelGroupEdge =
    isGroupBoundary &&
    resolvedSource.data?.state?.parent_uuid === facilityEquipmentGroupUuid &&
    resolvedTarget.data?.state?.parent_uuid === facilityEquipmentGroupUuid;

  // Group -> Group connections > connection/arrow color is gray(to clean up the canvas)
  const edgeColor = isSourceGroup || isTargetGroup ? 'var(--color-tertiary)' : handle.color;

  // A group's side of the handle id is keyed by group ownership (group connected to)
  const groupSideCounterpartId = rawId =>
    resolveGroupFromAnyId(rawId, groupList, facilityEquipmentGroupUuid)?.equip_group_uuid ?? rawId;

  const finalSourceHandleId = isSourceGroup
    ? `${resolvedSource.id}_${groupSideCounterpartId(handle.nodeId)}`
    : sourceHandleId;

  const finalTargetHandleId = isTargetGroup
    ? `${resolvedTarget.id}_${groupSideCounterpartId(sourceNodeId)}`
    : handle.id;

  // Skip a group-boundary handle id that hasn't been rendered on that node yet (new counterpart pairing, node tree hasn't rebuilt) — avoids React Flow's "couldn't create edge" error.
  const handleExists = (node, handleId) =>
    (node.data?.handles?.inputs ?? []).some(h => h.id === handleId) ||
    (node.data?.handles?.outputs ?? []).some(h => h.id === handleId);

  if (
    (isSourceGroup && !handleExists(resolvedSource, finalSourceHandleId)) ||
    (isTargetGroup && !handleExists(resolvedTarget, finalTargetHandleId))
  ) {
    return null;
  }

  // Side each handle should render on so it faces the node it's connected to.
  const sourceCenterX = getAbsoluteCenterX(resolvedSource, nodes);
  const targetCenterX = getAbsoluteCenterX(resolvedTarget, nodes);
  const sourceSide = targetCenterX < sourceCenterX ? 'left' : 'right';
  const targetSide = sourceCenterX < targetCenterX ? 'left' : 'right';

  return {
    id: `${resolvedSource.id}_${finalSourceHandleId}_${resolvedTarget.id}_${finalTargetHandleId}`,
    source: resolvedSource.id,
    sourceHandle: finalSourceHandleId,
    target: resolvedTarget.id,
    targetHandle: finalTargetHandleId,
    type: 'custom',
    selectable: true,
    label: getConnectionLabel({
      handle,
      sourceNode: resolvedSource,
      targetNode: resolvedTarget,
    }),
    markerEnd: { type: 'arrowclosed', color: edgeColor },
    data: {
      color: edgeColor,
      protocol: handle.protocol,
      sourceSide,
      targetSide,
      isGroupBoundary,
      topLevelGroupEdge,
    },
  };
};

export const buildEdgesForNode = ({
  nodeId,
  equipment,
  groups,
  nodes,
  facilityEquipmentGroupUuid,
}) => {
  const nodeData = findNodeData(nodeId, equipment, groups);

  if (!nodeData) {
    return [];
  }

  const handles = getHandlesFromNodeData(nodeData, equipment, groups, facilityEquipmentGroupUuid);

  const edges = getConnectedTargetHandles(handles)
    .map(handle => buildEdgeFromHandle(handle, nodes, groups, facilityEquipmentGroupUuid))
    .filter(Boolean);

  return edges;
};

/* ============================================================
   ===== EDGE PATH — HANDLE & STEP POSITION ==========
   ============================================================ */

export const MIN_STEP_POSITION = 0.15;
export const MAX_STEP_POSITION = 0.85;

// Ranks a handle among its same-side siblings on one node. Returns { rank, count } (rank is a
// 0-based index) rather than a normalized fraction, so getStepPosition can pair source and target
// ranks without losing sibling-count information.
export const getHandleRank = (node, handleId, position, direction) => {
  const siblings = (node?.internals?.handleBounds?.[direction] ?? [])
    .filter(h => h.position === position)
    .sort((a, b) => a.y - b.y);

  const rank = siblings.findIndex(h => h.id === handleId);
  if (rank === -1 || siblings.length < 2) {
    return null;
  }

  return { rank, count: siblings.length };
};

// Ranks a handle among all same-side handles of every sibling node sharing the same parent,
// so edges from different nodes within the same group each get a unique bend point (step).
// Returns { rank, count }
export const getHandleRankInParentContext = (
  node,
  handleId,
  position,
  direction,
  { getNodes, getInternalNode } = {}
) => {
  if (!node?.parentId || !getNodes || !getInternalNode) {
    return getHandleRank(node, handleId, position, direction);
  }

  const siblings = getNodes().filter(n => n.parentId === node.parentId);

  const allHandles = siblings.flatMap(sibling => {
    const internal = getInternalNode(sibling.id);
    const posY = internal?.internals?.positionAbsolute?.y ?? 0;
    return (internal?.internals?.handleBounds?.[direction] ?? [])
      .filter(h => h.position === position)
      .map(h => ({ nodeId: sibling.id, id: h.id, absoluteY: posY + h.y }));
  });

  if (allHandles.length < 2) {
    return getHandleRank(node, handleId, position, direction);
  }

  allHandles.sort((a, b) => a.absoluteY - b.absoluteY);

  const rank = allHandles.findIndex(h => h.nodeId === node.id && h.id === handleId);
  if (rank === -1) {
    return getHandleRank(node, handleId, position, direction);
  }

  return { rank, count: allHandles.length };
};

// Returns a step position between MIN_STEP_POSITION and MAX_STEP_POSITION for the source/target
// handle ranks ({ rank, count } from getHandleRank(InParentContext), or null).

export const getStepPosition = (source, target) => {
  if (!source && !target) {
    return 0.5;
  }

  let normalized;
  if (source && target) {
    const combinedIndex = source.rank * target.count + target.rank;
    const combinedMax = source.count * target.count - 1;
    normalized = combinedMax > 0 ? combinedIndex / combinedMax : 0.5;
  } else {
    const only = source || target;
    normalized = only.count > 1 ? only.rank / (only.count - 1) : 0.5;
  }

  return MIN_STEP_POSITION + normalized * (MAX_STEP_POSITION - MIN_STEP_POSITION);
};

// Handle path from the FROM point up over the top edge and down to the TO point — always via the top of the node.
export const buildTopRoutePath = (width, fromSide, fromY, toSide, toY) => {
  const fromX = fromSide === 'left' ? 0 : width;
  const toX = toSide === 'left' ? 0 : width;
  return `M ${fromX} ${fromY} L ${fromX} 0 L ${toX} 0 L ${toX} ${toY}`;
};
